import {
  ShowBase,
  useShowContext,
  useDelete,
  useNotify,
  useRedirect,
} from "ra-core";
import { useQuery } from "@tanstack/react-query";
import { ReferenceField } from "@/components/admin/reference-field";
import { TextField } from "@/components/admin/text-field";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router";
import { ArrowLeft, ExternalLink, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";

import { supabase } from "../providers/supabase/supabase";
import type { Entity } from "../types";

export const EntityShow = () => (
  <ShowBase>
    <EntityShowContent />
  </ShowBase>
);

const EntityShowContent = () => {
  const { record, isPending } = useShowContext<Entity>();
  const [deleteOne, { isPending: isDeleting }] = useDelete();
  const notify = useNotify();
  const redirect = useRedirect();

  // Check if entity is linked to any active (non-archived) account deal
  const { data: activeDeals } = useQuery({
    queryKey: ["entity_active_deals", record?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("deals")
        .select("id, name")
        .contains("entity_ids", [Number(record!.id)])
        .eq("type", "account")
        .is("archived_at", null);
      if (error) throw error;
      return data as { id: number; name: string }[];
    },
    enabled: !!record?.id,
  });

  const hasActiveDeals = (activeDeals?.length ?? 0) > 0;

  if (isPending || !record) return null;

  const handleDelete = () => {
    if (hasActiveDeals) {
      notify(
        `Cannot delete — linked to active deal: ${activeDeals![0].name}`,
        { type: "error" },
      );
      return;
    }
    deleteOne(
      "entities",
      { id: record.id, previousData: record },
      {
        onSuccess: () => {
          notify("Entity deleted", { type: "success" });
          redirect("list", "entities");
        },
        onError: () => {
          notify("Failed to delete entity", { type: "error" });
        },
      },
    );
  };

  return (
    <div className="mt-2 flex flex-col gap-4 max-w-3xl">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/entities" className="flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" />
            Back to Entities
          </Link>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDelete}
          disabled={isDeleting || hasActiveDeals}
          className="text-destructive hover:text-destructive"
          title={hasActiveDeals ? "Linked to an active account deal" : undefined}
        >
          <Trash2 className="h-4 w-4 mr-1" />
          Delete
        </Button>
      </div>

      <Card>
        <CardContent>
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-xl font-semibold">{record.name}</h2>
          </div>

          <div className="grid grid-cols-2 gap-x-8 gap-y-4 text-sm">
            <Section title="Identity">
              <Field label="Entity Name" value={record.entity_name} />
              <Field label="SPOC Code" value={record.spoc_code} />
              <Field label="Track ID" value={record.track_id} />
              <Field label="MPOB License" value={record.mpob_license} />
              {record.company_id && (
                <div className="flex flex-col">
                  <span className="text-muted-foreground">Company</span>
                  <ReferenceField
                    source="company_id"
                    reference="companies"
                    link="show"
                  >
                    <TextField source="name" className="font-medium" />
                  </ReferenceField>
                </div>
              )}
              {record.pdf_drive_link && (
                <div className="flex flex-col">
                  <span className="text-muted-foreground">Audit Report</span>
                  <a
                    href={record.pdf_drive_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                  >
                    View Audit Report
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
            </Section>

            <Section title="Location">
              <Field label="Address" value={record.address} />
              <Field label="State" value={record.state} />
              <Field label="Soil Type" value={record.soil_type} />
              {(record.gps_latitude || record.gps_longitude) && (
                <Field
                  label="GPS Coordinates"
                  value={`${record.gps_latitude ?? "—"}, ${record.gps_longitude ?? "—"}`}
                />
              )}
            </Section>

            <Section title="Area & Production">
              <Field
                label="Planted Area (Ha)"
                value={record.planted_area_ha?.toLocaleString()}
              />
              <Field
                label="Certified Area (Ha)"
                value={record.certified_area_ha?.toLocaleString()}
              />
              <Field
                label="Mature Area (Ha)"
                value={record.mature_ha?.toLocaleString()}
              />
              <Field
                label="Latest FFB (MT)"
                value={record.latest_ffb_mt?.toLocaleString()}
              />
              <Field
                label="OER %"
                value={
                  record.latest_oer_percent != null
                    ? `${record.latest_oer_percent}%`
                    : undefined
                }
              />
              <Field
                label="FFB/Ha Yield"
                value={record.ffb_per_ha?.toLocaleString()}
              />
              <Field
                label="Worker Total"
                value={record.worker_total?.toLocaleString()}
              />
            </Section>

            <Section title="Portal Lifecycle">
              <Field label="Portal Status" value={record.portal_status} />
              <Field label="First Seen" value={record.portal_first_seen} />
              <Field label="Last Seen" value={record.portal_last_seen} />
              <Field
                label="GPS Valid"
                value={
                  record.gps_valid != null
                    ? record.gps_valid
                      ? "Yes"
                      : "No"
                    : undefined
                }
              />
            </Section>

            {record.notes && (
              <Section title="Notes">
                <p className="whitespace-pre-wrap text-sm">{record.notes}</p>
              </Section>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-2">
    <h6 className="text-base font-semibold border-b pb-1 mb-1">{title}</h6>
    {children}
  </div>
);

const Field = ({ label, value }: { label: string; value?: string | null }) => (
  <div className="flex flex-col">
    <span className="text-muted-foreground">{label}</span>
    <span>{value || "—"}</span>
  </div>
);
